'use client';

import React, { useState, useEffect } from 'react';
import { MapPin, Plus, X, ChevronDown } from 'lucide-react';

interface District {
  id: number;
  name: string;
  blocks: Block[];
}

interface Block {
  id: number;
  name: string;
}

interface School {
  id: number;
  name: string;
  type: string;
}

interface Preference {
  district: string;
  block: string;
  school: string;
  scope: 'district' | 'block' | 'school';
}

interface PreferenceCardsProps {
  preferences: Preference[];
  setPreferences: React.Dispatch<React.SetStateAction<Preference[]>>;
  /** Derived from the class band by the parent — displayed here, never edited. */
  schoolType: 'primary' | 'other';
}

const SCOPES: { value: 'district' | 'block' | 'school'; label: string; hi: string }[] = [
  { value: 'district', label: 'Whole district', hi: 'पूरा जिला' },
  { value: 'block', label: 'Specific block', hi: 'ब्लॉक' },
  { value: 'school', label: 'Specific school', hi: 'स्कूल' },
];

export default function PreferenceCards({
  preferences,
  setPreferences,
  schoolType,
}: PreferenceCardsProps) {
  const [districts, setDistricts] = useState<District[]>([]);
  const [blocks, setBlocks] = useState<Block[][]>([]);
  const [schools, setSchools] = useState<School[][]>([]);
  const [expandedIdx, setExpandedIdx] = useState(0);

  // Fetch districts on mount
  useEffect(() => {
    fetch('/api/bihar/districts')
      .then(res => res.json())
      .then(data => setDistricts(data))
      .catch(err => console.error('Failed to fetch districts:', err));
  }, []);

  // Fetch blocks for each preference
  useEffect(() => {
    const fetchBlocks = async () => {
      const blocksData = await Promise.all(
        preferences.map(async (pref) => {
          if (pref.district) {
            try {
              const res = await fetch('/api/bihar/districts');
              const data = await res.json();
              const found = data.find((d: District) => d.name === pref.district);
              return found ? found.blocks : [];
            } catch (err) {
              console.error('Failed to fetch blocks:', err);
              return [];
            }
          }
          return [];
        })
      );
      setBlocks(blocksData);
    };
    fetchBlocks();
  }, [preferences]);

  // Fetch schools for each preference
  useEffect(() => {
    const fetchSchools = async () => {
      const schoolsData = await Promise.all(
        preferences.map(async (pref) => {
          if (pref.district && pref.block) {
            try {
              const res = await fetch(`/api/bihar/schools?district=${encodeURIComponent(pref.district)}&block=${encodeURIComponent(pref.block)}&context=form`);
              const data = await res.json();
              if (Array.isArray(data)) {
                return data.filter((s: School) =>
                  schoolType === 'primary' ? s.type === 'primary' : s.type !== 'primary'
                );
              }
              return [];
            } catch (err) {
              console.error('Failed to fetch schools:', err);
              return [];
            }
          }
          return [];
        })
      );
      setSchools(schoolsData);
    };
    fetchSchools();
  }, [preferences, schoolType]);

  const handlePrefChange = (idx: number, field: string, value: string) => {
    setPreferences(prev => {
      const updated = [...prev];
      updated[idx] = { ...updated[idx], [field]: value };

      // Reset dependent fields
      if (field === 'district') {
        updated[idx].block = '';
        updated[idx].school = '';
        updated[idx].scope = 'district';
      } else if (field === 'block') {
        updated[idx].school = '';
        // Selecting a block only widens from district → block; keep 'school' intent.
        if (updated[idx].scope === 'district') updated[idx].scope = 'block';
      } else if (field === 'scope' && value === 'district') {
        updated[idx].block = '';
        updated[idx].school = '';
      } else if (field === 'scope' && value === 'block') {
        updated[idx].school = '';
      }

      return updated;
    });
  };

  const addPreference = () => {
    const last = preferences[preferences.length - 1];
    if (!last?.district) {
      alert('Please select a district for the current preference before adding a new one.\nकृपया नई प्राथमिकता जोड़ने से पहले वर्तमान प्राथमिकता के लिए एक जिला चुनें।');
      return;
    }
    if (preferences.length < 10) {
      setPreferences(prev => [...prev, { district: '', block: '', school: '', scope: 'district' }]);
      setExpandedIdx(preferences.length);
    }
  };

  const removePreference = (idx: number) => {
    if (preferences.length > 1) {
      setPreferences(prev => prev.filter((_, i) => i !== idx));
      setExpandedIdx(prev => (idx <= prev ? Math.max(0, prev - 1) : prev));
    }
  };

  const scopeSummary = (pref: Preference) => {
    if (pref.scope === 'school') return pref.school ? `School: ${pref.school}` : 'Specific school / स्कूल';
    if (pref.scope === 'block') return pref.block ? `Block: ${pref.block}` : 'Specific block / ब्लॉक';
    return 'Whole district / पूरा जिला';
  };

  // Only block EXACT duplicates — the same district can still be reused for a
  // different block/school. A district is hidden only when another preference
  // already covers it as a "whole district" (picking it would repeat that).
  const districtOptions = (idx: number) => {
    const wholeTaken = new Set(
      preferences.filter((p, i) => i !== idx && p.scope === 'district' && p.district).map(p => p.district)
    );
    return districts.filter(d => !wholeTaken.has(d.name));
  };

  // Hide a block only if another preference already uses that exact district+block
  // at block-scope. (School-scope keeps every block so you can pick a school in it.)
  const blockOptions = (idx: number) => {
    const pref = preferences[idx];
    const list = blocks[idx] || [];
    if (pref.scope !== 'block') return list;
    const taken = new Set(
      preferences
        .filter((p, i) => i !== idx && p.scope === 'block' && p.district === pref.district && p.block)
        .map(p => p.block)
    );
    return list.filter(b => !taken.has(b.name));
  };

  // Hide a school only if another preference already uses that exact
  // district+block+school.
  const schoolOptions = (idx: number) => {
    const pref = preferences[idx];
    const list = schools[idx] || [];
    const taken = new Set(
      preferences
        .filter((p, i) => i !== idx && p.scope === 'school' && p.district === pref.district && p.block === pref.block && p.school)
        .map(p => p.school)
    );
    return list.filter(s => !taken.has(s.name));
  };

  const selectClasses =
    'w-full px-3 py-2.5 border border-zinc-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 transition-all duration-200 bg-white text-zinc-900 shadow-sm text-sm';

  return (
    <div className="space-y-3 mt-2">
      {/* Header */}
      <div className="flex items-center space-x-3">
        <div className="w-8 h-8 sm:w-10 sm:h-10 bg-gradient-to-r from-emerald-200 to-green-300 rounded-full flex items-center justify-center shadow-lg">
          <MapPin className="h-5 w-5 sm:h-6 sm:w-6 text-green-700" />
        </div>
        <div>
          <h3 className="text-lg sm:text-xl lg:text-lg font-bold text-zinc-900">Preferred Transfer Choices</h3>
          <p className="text-zinc-700 text-xs sm:text-sm">अपनी पसंदीदा स्थानांतरण प्राथमिकताएँ चुनें</p>
        </div>
      </div>

      {/*
        School type is no longer a choice — it follows from the class band
        (schoolTypeForClass): 1-5 is primary, anything above it is not. As a
        radio pair it was routinely answered inconsistently with the class given
        further up the same form, and the consequence is invisible and severe:
        it decides which schools a teacher can ever be matched against.
        Shown, not asked, so they can still catch a wrong class.
      */}
      <div className="flex flex-wrap items-center gap-x-2 gap-y-1 rounded-xl border border-zinc-200 bg-zinc-50 px-3 py-2 text-sm">
        <span className="font-semibold text-zinc-800">School Type / स्कूल प्रकार:</span>
        <span className="font-semibold text-emerald-700">
          {schoolType === 'primary' ? 'Primary / प्राथमिक' : 'Other / अन्य'}
        </span>
        <span className="text-zinc-500 text-xs">— आपके चुने हुए वर्ग के अनुसार</span>
      </div>

      {/* Preference list */}
      <div className="space-y-2">
        {preferences.map((pref, idx) => {
          const open = expandedIdx === idx;
          const complete = !!pref.district;
          return (
            <div
              key={idx}
              className={`rounded-xl border bg-white overflow-hidden transition-all duration-200 ${open ? 'border-emerald-300 shadow-sm' : 'border-zinc-200'}`}
            >
              {/* Row header — shows what's selected */}
              <div className="flex items-center gap-2 p-2.5">
                <span className={`shrink-0 w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold ${complete ? 'bg-emerald-600 text-white' : 'bg-zinc-200 text-zinc-500'}`}>
                  {idx + 1}
                </span>
                <button
                  type="button"
                  onClick={() => setExpandedIdx(open ? -1 : idx)}
                  className="flex-1 min-w-0 text-left"
                >
                  {complete ? (
                    <>
                      <div className="text-sm font-semibold text-zinc-900 truncate">{pref.district}</div>
                      <div className="text-xs text-zinc-500 truncate">{scopeSummary(pref)}</div>
                    </>
                  ) : (
                    <div className="text-sm text-zinc-500">Tap to choose a district / जिला चुनें</div>
                  )}
                </button>
                {preferences.length > 1 && (
                  <button
                    type="button"
                    onClick={() => removePreference(idx)}
                    aria-label="Remove preference"
                    className="shrink-0 p-1.5 text-red-500 hover:bg-red-50 rounded-lg transition-colors"
                  >
                    <X className="h-4 w-4" />
                  </button>
                )}
                <button
                  type="button"
                  onClick={() => setExpandedIdx(open ? -1 : idx)}
                  aria-label="Toggle preference"
                  className="shrink-0 p-1"
                >
                  <ChevronDown className={`h-4 w-4 text-zinc-400 transition-transform duration-200 ${open ? 'rotate-180' : ''}`} />
                </button>
              </div>

              {/* Inline editor */}
              {open && (
                <div className="px-3 pb-3 pt-1 border-t border-zinc-100 space-y-3">
                  {/* District */}
                  <div className="space-y-1.5">
                    <label className="block text-xs font-semibold text-zinc-700">District / जिला *</label>
                    <select
                      value={pref.district}
                      onChange={e => handlePrefChange(idx, 'district', e.target.value)}
                      className={selectClasses}
                    >
                      <option value="">Select District</option>
                      {districtOptions(idx).map(d => (
                        <option key={d.id} value={d.name}>{d.name}</option>
                      ))}
                    </select>
                  </div>

                  {pref.district && (
                    <>
                      {/* Scope pills */}
                      <div className="space-y-1.5">
                        <label className="block text-xs font-semibold text-zinc-700">Match scope / मैच का दायरा</label>
                        <div className="grid grid-cols-3 gap-1.5">
                          {SCOPES.map(s => {
                            const active = pref.scope === s.value;
                            return (
                              <button
                                key={s.value}
                                type="button"
                                onClick={() => handlePrefChange(idx, 'scope', s.value)}
                                className={`px-2 py-1.5 rounded-lg border text-center transition-all duration-200 ${active ? 'bg-emerald-600 border-emerald-600 text-white' : 'bg-white border-zinc-200 text-zinc-700 hover:border-emerald-300'}`}
                              >
                                <div className="text-xs font-semibold leading-tight">{s.label}</div>
                                <div className="text-[10px] opacity-90 leading-tight">{s.hi}</div>
                              </button>
                            );
                          })}
                        </div>
                        <p className={`text-xs ${pref.scope === 'district' ? 'text-emerald-600' : pref.scope === 'block' ? 'text-amber-600' : 'text-pink-600'}`}>
                          {pref.scope === 'district'
                            ? 'Highest match chance · सबसे ज़्यादा मौका'
                            : pref.scope === 'block'
                              ? 'Narrower match · कम विकल्प'
                              : 'Lowest match chance · सबसे कम मौका'}
                        </p>
                      </div>

                      {/* Block */}
                      {pref.scope !== 'district' && (
                        <div className="space-y-1.5">
                          <label className="block text-xs font-semibold text-zinc-700">Block / ब्लॉक *</label>
                          <select
                            value={pref.block}
                            onChange={e => handlePrefChange(idx, 'block', e.target.value)}
                            className={selectClasses}
                          >
                            <option value="">Select Block</option>
                            {blockOptions(idx).map(b => (
                              <option key={b.id} value={b.name}>{b.name}</option>
                            ))}
                          </select>
                        </div>
                      )}

                      {/* School */}
                      {pref.scope === 'school' && pref.block && (
                        <div className="space-y-1.5">
                          <label className="block text-xs font-semibold text-zinc-700">School / स्कूल *</label>
                          <select
                            value={pref.school}
                            onChange={e => handlePrefChange(idx, 'school', e.target.value)}
                            className={selectClasses}
                          >
                            <option value="">
                              {schoolOptions(idx).length > 0
                                ? `Select School (${schoolOptions(idx).length} available)`
                                : 'No schools available'}
                            </option>
                            {schoolOptions(idx).map(s => (
                              <option key={s.id} value={s.name}>{s.name}</option>
                            ))}
                          </select>
                        </div>
                      )}
                    </>
                  )}
                </div>
              )}
            </div>
          );
        })}
      </div>

      {/* Add preference */}
      {preferences.length < 10 && (
        <button
          type="button"
          onClick={addPreference}
          className="w-full flex items-center justify-center gap-2 px-4 py-2.5 rounded-xl border-2 border-dashed border-emerald-300 text-emerald-700 font-semibold text-sm hover:bg-emerald-50 transition-all duration-200"
        >
          <Plus className="h-4 w-4" />
          Add preference / और प्राथमिकता जोड़ें
          <span className="text-xs font-normal text-zinc-400">({preferences.length}/10)</span>
        </button>
      )}
    </div>
  );
}
